import type { DateRange } from 'react-day-picker';
/**
 * 날짜 처리 관련 유틸리티 함수들
 */

/**
 * 로컬 시간 기준으로 YYYY-MM-DD 형식 날짜 문자열 생성
 * @param date Date 객체
 * @returns YYYY-MM-DD 형식의 문자열
 */
export const formatLocalDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * YYYYMM 형식을 "YYYY년 M월" 형식으로 변환
 * @param yearMonth YYYYMM 형식의 문자열
 * @returns "YYYY년 M월" 형식의 문자열
 */
export const formatYearMonth = (yearMonth: string): string => {
  if (!yearMonth || yearMonth.length !== 6) {
    return yearMonth || '미분류';
  }
  
  const year = yearMonth.substring(0, 4);
  const month = parseInt(yearMonth.substring(4, 6));
  return `${year}년 ${month}월`;
};

/**
 * 날짜 범위 문자열 생성
 * @param startDate 시작 날짜
 * @param endDate 종료 날짜
 * @returns "YYYY.MM.DD ~ YYYY.MM.DD" 형식의 문자열
 */
export const formatDateRange = (startDate: Date, endDate: Date): string => {
  return `${startDate.toLocaleDateString('ko-KR')} ~ ${endDate.toLocaleDateString('ko-KR')}`;
};

/**
 * 오늘부터 지정된 일수만큼 이전 날짜 범위 계산
 * @param days 이전 일수
 * @returns { fromDate, toDate }
 */
export const calculateDateRange = (days: number): { fromDate: Date; toDate: Date } => {
  const toDate = new Date();
  const fromDate = new Date();
  fromDate.setDate(toDate.getDate() - days + 1);
  
  return { fromDate, toDate };
};

/**
 * 이번 달 첫날과 마지막날 계산
 * @returns { fromDate, toDate }
 */
export const getCurrentMonthRange = (): { fromDate: Date; toDate: Date } => {
  const now = new Date();
  const fromDate = new Date(now.getFullYear(), now.getMonth(), 1);
  const toDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  
  return { fromDate, toDate };
};

/**
 * 지난달 첫날과 마지막날 계산
 * @returns { fromDate, toDate }
 */
export const getLastMonthRange = (): { fromDate: Date; toDate: Date } => {
  const now = new Date();
  const fromDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const toDate = new Date(now.getFullYear(), now.getMonth(), 0);
  
  return { fromDate, toDate };
};



/**
 * 월의 날짜 범위(DateRange)
 */
export const getMonthDateRange = (date: Date = new Date()): DateRange => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const lastDay = new Date(year, month + 1, 0).getDate();
  return {
    from: new Date(year, month, 1),
    to: new Date(year, month, lastDay),
  };
};

/**
 * 현재 월의 날짜 범위(DateRange)
 */
export const getCurrentMonthDateRange = (): DateRange => {
  return getMonthDateRange(new Date());
};

/**
 * 이전 월의 날짜 범위(DateRange)
 */
export const getPreviousMonthDateRange = (): DateRange => {
  const now = new Date();
  const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return getMonthDateRange(prevMonth);
};

/**
 * 다음 월의 날짜 범위(DateRange)
 */
export const getNextMonthDateRange = (): DateRange => {
  const now = new Date();
  const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  return getMonthDateRange(nextMonth);
};

/**
 * 특정 연/월의 날짜 범위(DateRange)
 */
export const getSpecificMonthDateRange = (year: number, month: number): DateRange => {
  const date = new Date(year, month, 1);
  return getMonthDateRange(date);
};

/**
 * DateRange 유효성 검사
 */
export const isValidDateRange = (dateRange: DateRange | undefined): boolean => {
  if (!dateRange || !dateRange.from || !dateRange.to) {
    return false;
  }
  return dateRange.from <= dateRange.to;
};

/**
 * DateRange의 일수 계산(포함)
 */
export const getDateRangeDays = (dateRange: DateRange | undefined): number => {
  if (!dateRange || !dateRange.from || !dateRange.to) {
    return 0;
  }
  const from = dateRange.from;
  const to = dateRange.to;
  const timeDiff = to.getTime() - from.getTime();
  const dayDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
  return dayDiff + 1;
};

/**
 * ISO(YYYY-MM-DD) 문자열 변환
 */
export const formatDateToString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}; 



const getFutureWeekdayFrom = (daysFromNow: number): Date => {
  const today = new Date();
  const checkDate = new Date(today.getTime());

  // 지정된 일수만큼 뒤로 이동하여 확인 시작
  checkDate.setDate(checkDate.getDate() + daysFromNow);

  while (true) {
    const dayOfWeek = checkDate.getDay();
    // 주말(토요일: 6, 일요일: 0)이 아니면 해당 날짜를 반환합니다.
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      return checkDate;
    }
    // 주말이면 하루를 더해 다음 날을 확인합니다.
    checkDate.setDate(checkDate.getDate() + 1);
  }
};

/**
 * 당일제외 가장 미래의 평일 반환 - 주말 제외
 */
export const getFutureWeekday = (): Date => {
  return getFutureWeekdayFrom(1); // 내일부터 시작
};

/**
 * 당일과 명일 제외 가장 미래의 평일 반환 - 주말 제외
 */
export const getFutureWeekdayExceptTodayAndTomorrow = (): Date => {
  return getFutureWeekdayFrom(2); // 모레부터 시작
};